// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MAXIMUM_DATE, MINIMUM_DATE } from '@/constants/date-shortcut';
import { EditRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useRef, useState } from 'react';

interface Props {
  /**
   * The current date value to display. If null, shows "not set".
   */
  date: DateTime | null;

  /**
   * Callback to save the new date. Should return a promise that resolves on success.
   */
  onSave: (newDate: DateTime) => Promise<void>;

  /**
   * Minimum date that can be selected (e.g., shippedAt for delivery date validation).
   */
  minDate?: DateTime | null;

  /**
   * Maximum date that can be selected (e.g., deliveredAt for ship date validation).
   */
  maxDate?: DateTime | null;

  /**
   * Error message rendered when the selected date is before minDate.
   * Defaults to shipment-centric copy kept for back-compat with existing
   * callers; pass a domain-appropriate message when reusing the component
   * outside of shipment phases.
   */
  minDateMessage?: string;

  /**
   * Error message rendered when the selected date is after maxDate. See
   * `minDateMessage` for default behavior and overrides.
   */
  maxDateMessage?: string;

  /**
   * Persistent helper text rendered below the picker when there is no
   * validation error. Useful for surfacing the underlying constraint (for
   * example "Cannot be after today") while the input is still valid.
   */
  helperText?: string;

  /**
   * Label for the popover title (e.g., "Delivery date").
   */
  label?: string;

  /**
   * When true (default) the picker blocks any time past "now". Callers that
   * enforce a different upper bound via `maxDate` — e.g. end-of-today to
   * allow picking later hours of the current day — should pass false.
   */
  disableFuture?: boolean;
}

/**
 * Formats a DateTime for display in the phase indicator subtext.
 */
function formatDateSubtext(date: DateTime): string {
  return date.toFormat('MMM d, yyyy');
}

/**
 * A component that displays a date with an edit icon that opens a popover
 * for inline editing. Used in phase indicators to allow editing dates
 * like delivery date.
 */
export function EditablePhaseDate(props: Props) {
  const popover = useDisclosure();
  const anchorRef = useRef<HTMLDivElement>(null);

  // Get browser's timezone for display
  const browserTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Local state for the date being edited
  // Default to current time if no date is set
  const [editDate, setEditDate] = useState<DateTime | null>(
    props.date ??
      DateTime.now()
        .setZone(browserTimeZoneName)
        .set({ second: 0, millisecond: 0 })
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async (newDate: DateTime) => {
      // Ensure seconds and milliseconds are 0
      const dateToSave = newDate.set({ second: 0, millisecond: 0 });
      return props.onSave(dateToSave);
    },
    onSuccess: () => {
      setValidationError(null);
      popover.close();
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);

      // Neither 2205 nor 2220 has a curated ERROR_MESSAGES entry, so
      // hardcode the user-facing copy rather than reading
      // apiError.message (which now returns the raw 'unknown error'
      // schema default for unmapped codes).
      if (apiError.apiStatusCode === 2205) {
        setValidationError(
          'Delivery date cannot be before the date the shipment was shipped.'
        );
        setShowSnackbar(false);
      } else if (apiError.apiStatusCode === 2220) {
        setValidationError('Ship date cannot be after the delivery date.');
        setShowSnackbar(false);
      } else {
        // All other errors should show SupportSnackbar
        setShowSnackbar(true);
      }
    },
  });

  const handleOpen = () => {
    // Reset to the current prop value when opening, or current time if not set
    setEditDate(
      props.date ??
        DateTime.now()
          .setZone(browserTimeZoneName)
          .set({ second: 0, millisecond: 0 })
    );
    setValidationError(null);
    popover.open();
  };

  const handleClose = () => {
    setValidationError(null);
    saveMutation.reset();
    popover.close();
  };

  const handleDateChange = (newDate: DateTime | null) => {
    if (newDate === null) {
      setEditDate(null);
      return;
    }

    // If the incoming dateTime doesn't have a proper IANA timezone, set it to the browser timezone
    if (
      !newDate.zoneName ||
      newDate.zoneName === 'UTC' ||
      newDate.zoneName.includes('UTC')
    ) {
      newDate = newDate.setZone(browserTimeZoneName);
    }

    // Always set seconds and milliseconds to 0
    newDate = newDate.set({ second: 0, millisecond: 0 });

    setEditDate(newDate);
    // Clear validation error when user changes date
    setValidationError(null);
  };

  const handleSave = () => {
    if (!editDate) {
      setValidationError('Date is required');
      return;
    }

    // Client-side validation for minDate
    if (props.minDate && editDate < props.minDate) {
      setValidationError(
        props.minDateMessage ?? 'Date cannot be before ship date'
      );
      return;
    }

    // Client-side validation for maxDate
    if (props.maxDate && editDate > props.maxDate) {
      setValidationError(
        props.maxDateMessage ?? 'Date cannot be after delivery date'
      );
      return;
    }

    saveMutation.mutate(editDate);
  };

  const label = props.label ?? 'Edit date';

  return (
    <>
      <Stack ref={anchorRef} direction="row" alignItems="center" spacing={0.25}>
        {props.date ? (
          <MultiTimezoneTooltip timestamp={props.date} placement="bottom">
            {formatDateSubtext(props.date)}
          </MultiTimezoneTooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            not set
          </Typography>
        )}
        <IconButton size="small" onClick={handleOpen} sx={{ padding: 0.25 }}>
          <EditRounded sx={{ fontSize: '0.875rem' }} color="action" />
        </IconButton>
      </Stack>

      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          saveMutation.reset();
        }}
        message="An error occurred while updating the date."
      />

      <Popover
        open={popover.isOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { marginTop: 1 },
          },
        }}
      >
        <Box p={2} width={320}>
          <Typography variant="subtitle2" mb={2}>
            {label}
          </Typography>

          <DateTimePicker
            format="yyyy-MM-dd hh:mm a"
            disableFuture={props.disableFuture ?? true}
            minDateTime={props.minDate ?? MINIMUM_DATE}
            maxDateTime={props.maxDate ?? MAXIMUM_DATE}
            value={editDate}
            onChange={handleDateChange}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: Boolean(validationError),
                helperText: validationError ?? props.helperText,
                autoComplete: 'off',
              },
            }}
          />

          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
            <Button size="small" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleSave}
              loading={saveMutation.isPending}
            >
              Save
            </LoadingButton>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
